import { useMantineTheme } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { EntityType } from '@repo/common/enums/entityType';
import { Icon, Text } from '@repo/foundations';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	usePolicyEnabled,
	useQuestionsEnabled,
	useSuspenseFeatureAccess,
} from '../../api';
import {
	useSuspenseDatabase,
	useUpdateDatabase,
} from '../../api/hooks/database';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { StaticProperty } from '../../components/EntityPageLayout/EntityPropertySidebar';
import {
	getDefaultTab,
	getOrderedTabs,
} from '../../components/EntityPageLayout/utils';
import EntityPolicyList from '../../components/EntityPolicyList/EntityPolicyList';
import type { OverviewPageTab } from '../../components/Overview/OverviewPageTabs';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import type { DjangoValueType } from '../TemplatePage/types';
import SchemasTab from './components/SchemasTab';
import TablesTab from './components/TablesTab';

const InnerDatabasePage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const hasPolicyAccess = usePolicyEnabled();
	const questionsEnabled = useQuestionsEnabled();
	const theme = useMantineTheme();
	const { secodaCoreAccess } = useSuspenseFeatureAccess();
	const { data: database } = useSuspenseDatabase({
		id,
	});

	useMount(() => {
		trackEvent('database/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateDatabase({});

	const updateDatabase = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely && database) {
				await mutateAsync({
					data: {
						id: database.id,
						[key]: value,
					},
				});
				trackEvent('database/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, database, user, workspace]
	);

	if (!database) {
		return <EntityPageLayoutSkeleton />;
	}

	const documentationFirst = (database.definition?.length || 0) > 2;

	const navigateToTab = useTabNavigate();

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			navigateToTab(updatedTab);
		},
		[navigateToTab]
	);

	const tabs: OverviewPageTab[] = useMemo(
		() => [
			{
				value: 'schemas',
				label: 'Schemas',
				component: <SchemasTab databaseId={database.id} />,
			},
			{
				value: 'tables',
				label: 'Tables',
				component: (
					<TablesTab
						databaseId={database.id}
						integrationId={database.integration}
					/>
				),
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: questionsEnabled && !!secodaCoreAccess,
				component: <EntityDiscussions entity={database} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={database} />,
			},
			{
				value: 'policies',
				label: 'Policies',
				condition: hasPolicyAccess,
				component: <EntityPolicyList entity={database} />,
			},
		],
		[database, hasPolicyAccess, secodaCoreAccess, questionsEnabled]
	);

	const orderedTabs: OverviewPageTab[] = useMemo(
		() => getOrderedTabs(tabs, documentationFirst),
		[tabs, documentationFirst]
	);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(orderedTabs), [orderedTabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={database.id}
			name="Database"
			isReadOnlyTitle
			entity={database}
			icon={<EntityLogo size={32} entity={database} />}
			updateEntity={updateDatabase}
			withDescription
			withFrequentUsers
			withOwnerSelector
			withCollectionSelector
			withCustomPropertyEditors
			withTagSelector
			withEntityPopularity
			withRelatedResourceSelector
			withCustomPublishElement={
				<RichTooltip
					title="Read-only"
					body={<Text size="sm">Databases are always published.</Text>}
					position="left"
				>
					<StaticProperty
						type="badge"
						label="Status"
						value={{
							label: 'Published',
							value: true,
							icon: <Icon name="circleCheckFilled" />,
							iconColor: theme.other.getColor('icon/success/default'),
							fill: 'surface/primary/disabled',
						}}
					/>
				</RichTooltip>
			}
			{...getEntityPageSidebarProps(EntityType.database)}
		>
			<OverviewPageTabs
				key={database.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={orderedTabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// By memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function DatabasePage() {
	const id = useParamsIdSuffixUuid();

	return <InnerDatabasePage id={id} />;
}
